import {CommonModule} from '@angular/common';
import {Component, DestroyRef, computed, effect, inject, Input, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faCartShopping, faPlus} from '@fortawesome/free-solid-svg-icons';

import {AppLogger, AppLoggerService} from '../../core/logging';
import {RecentTripItem, ShoppingListWithItems} from '../../models/shopping-list';
import {ActiveIconPackService} from '../../services/active-icon-pack.service';
import {ThemeService} from '../../services/theme.service';
import {ShoppingService} from '../../services/shopping/shopping.service';
import {ShoppingSyncService} from '../../services/shopping/shopping-sync.service';
import {UiButtonComponent, UiInputComponent, UiToastService} from '../../shared/ui';

/** Source of a quick-add autosuggestion, used to colorize the entry. */
export type QuickAddSuggestionSource = 'active' | 'bought' | 'past';

/** A single de-duplicated entry in the quick-add autosuggest dropdown. */
export interface QuickAddSuggestion {
  /** Stable de-dupe key (product_key when known, else normalized title). */
  key: string;
  title: string;
  product_key?: string | null;
  product_id?: number | null;
  /** Set for entries that already exist on the active list. */
  client_id?: string;
  source: QuickAddSuggestionSource;
}

/**
 * Self-contained shopping-list quick-add with the same autosuggest as the
 * shopping page: it reads the active list from the shared `ShoppingSyncService`
 * and seeds "past" suggestions from recent trips. Given a `householdId` it
 * resolves (and, if needed, creates) the household's active list and loads it
 * into the sync service, so it works on the dashboard without the full
 * shopping page being mounted.
 */
@Component({
  selector: 'app-shopping-quick-add',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, UiInputComponent, UiButtonComponent],
  templateUrl: './shopping-quick-add.component.html',
})
export class ShoppingQuickAddComponent {
  private readonly shoppingService = inject(ShoppingService);
  private readonly sync = inject(ShoppingSyncService);
  private readonly activeIconPack = inject(ActiveIconPackService);
  private readonly themeService = inject(ThemeService);
  private readonly toast = inject(UiToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly log: AppLogger = inject(AppLoggerService).createForClass(ShoppingQuickAddComponent);

  /**
   * Household to add items for. Setting it resolves the household's active
   * shopping list and loads it into the shared sync service.
   */
  @Input()
  set householdId(value: number | null | undefined) {
    if (value == null || value === this._householdId) return;
    this._householdId = value;
    this.activeListId = null;
    this.ensureActiveList(value);
    this.loadRecentTripItems(value);
  }

  private _householdId: number | null = null;
  private activeListId: number | null = null;

  /** Items signal from the shared sync service (the active list). */
  readonly items = this.sync.items;

  /**
   * In-flight adds keyed by `client_id`, each holding the loading toast to
   * resolve and the label to show. Watched by {@link syncToastWatcher}, which
   * flips the toast to success/error once the item's `sync_status` settles.
   */
  private readonly pendingToasts = signal<Map<string, { toastId: string; label: string }>>(new Map());

  /** True while at least one add is still syncing — drives the button spinner. */
  protected readonly adding = computed(() => this.pendingToasts().size > 0);

  quickAddText = '';
  quickAddFocused = false;
  /** Index of the keyboard-highlighted suggestion, or -1 when none. */
  activeIndex = -1;
  /** Suggestion shown in the box (picked but not yet added). */
  private selectedSuggestion: QuickAddSuggestion | null = null;
  recentTripItems: RecentTripItem[] = [];
  private blurTimer: ReturnType<typeof setTimeout> | null = null;

  protected readonly faCartShopping = faCartShopping;
  protected readonly faPlus = faPlus;

  constructor() {
    // Resolve each pending add's loading toast once its item's sync settles.
    // Adds are optimistic (the item appears instantly), so the toast/spinner
    // is our only feedback on the dashboard, where no list is rendered.
    effect(() => {
      const items = this.items();
      const pending = this.pendingToasts();
      if (pending.size === 0) return;

      const resolved: string[] = [];
      for (const [clientId, {toastId, label}] of pending) {
        const item = items.find(i => i.client_id === clientId);
        if (!item) {
          // Item vanished before we saw it sync (list switch/clear) — drop it.
          this.toast.dismiss(toastId);
          resolved.push(clientId);
        } else if (item.sync_status === 'synced') {
          this.toast.update(toastId, `„${label}" hinzugefügt`, {variant: 'success'});
          resolved.push(clientId);
        } else if (item.sync_status === 'failed') {
          this.toast.update(toastId, `„${label}" konnte nicht hinzugefügt werden`, {variant: 'error'});
          resolved.push(clientId);
        }
      }

      if (resolved.length > 0) {
        this.pendingToasts.update(m => {
          const next = new Map(m);
          for (const id of resolved) next.delete(id);
          return next;
        });
      }
    });
  }

  /** Open a loading toast for an in-flight add and track it by `client_id`. */
  private trackAdd(clientId: string, label: string): void {
    const toastId = this.toast.loading(`„${label}" wird hinzugefügt …`);
    this.pendingToasts.update(m => {
      const next = new Map(m);
      next.set(clientId, {toastId, label});
      return next;
    });
  }

  // ── List bootstrapping ──────────────────────────────────────────────────────

  private ensureActiveList(householdId: number): void {
    this.shoppingService.getLists(householdId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: lists => {
          const active = lists.find(l => l.is_active) ?? lists[0];
          if (active) {
            this.loadListItems(householdId, active.id);
          } else {
            this.createDefaultList(householdId);
          }
        },
        error: err => this.log.error('Failed to load shopping lists', err),
      });
  }

  private createDefaultList(householdId: number): void {
    this.shoppingService.createList(householdId, {name: 'Einkaufsliste'})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: list => this.loadListItems(householdId, list.id),
        error: err => this.log.error('Failed to create default shopping list', err),
      });
  }

  private loadListItems(householdId: number, listId: number): void {
    this.shoppingService.getList(listId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (list: ShoppingListWithItems) => {
          this.activeListId = listId;
          this.sync.setActiveList(householdId, listId, list.items ?? []);
        },
        error: err => this.log.error('Failed to load shopping list items', err, {listId}),
      });
  }

  private loadRecentTripItems(householdId: number): void {
    this.shoppingService.getRecentTripItems(householdId, 5)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: items => this.recentTripItems = items ?? [],
        error: () => this.recentTripItems = [],
      });
  }

  // ── Quick-add ───────────────────────────────────────────────────────────────

  onQuickAddInput(value: string): void {
    this.quickAddText = value;
    // Editing the text invalidates any picked suggestion and highlight.
    this.selectedSuggestion = null;
    this.activeIndex = -1;
    // Typing implies focus. After picking a suggestion we hide the dropdown
    // without blurring, so re-arm focus here so further typing re-opens it.
    if (this.blurTimer) { clearTimeout(this.blurTimer); this.blurTimer = null; }
    this.quickAddFocused = true;
  }

  clearQuickAdd(): void {
    this.quickAddText = '';
    this.selectedSuggestion = null;
    this.activeIndex = -1;
  }

  quickAdd(): void {
    if (this.activeListId == null) return;
    const text = this.quickAddText.trim();
    if (!text) return;

    const chosen = this.selectedSuggestion;
    if (chosen && chosen.title === text) {
      // Confirming a picked suggestion → keep its source-specific behavior
      // (re-activate a bought item, no-op one already listed, keep product).
      this.commitSuggestion(chosen);
    } else {
      // Free text, or edited after picking → add as raw input.
      const clientId = this.sync.enqueueRawInput(text);
      this.trackAdd(clientId, text);
    }

    this.clearQuickAdd();
    this.quickAddFocused = false;
  }

  // ── Autosuggest ─────────────────────────────────────────────────────────────

  onQuickAddFocus(): void {
    if (this.blurTimer) { clearTimeout(this.blurTimer); this.blurTimer = null; }
    this.quickAddFocused = true;
  }

  onQuickAddBlur(): void {
    // Delay so a click on a suggestion (which steals focus) still registers.
    this.blurTimer = setTimeout(() => { this.quickAddFocused = false; }, 150);
  }

  /** ↓ moves the highlight down (opening the dropdown if needed). */
  onArrowDown(event: Event): void {
    const count = this.quickAddSuggestions.length;
    if (count === 0) return;
    event.preventDefault();
    this.quickAddFocused = true;
    this.activeIndex = Math.min(this.activeIndex + 1, count - 1);
  }

  /** ↑ moves the highlight up (clamped at the top entry). */
  onArrowUp(event: Event): void {
    if (!this.showQuickAddSuggestions) return;
    event.preventDefault();
    this.activeIndex = Math.max(this.activeIndex - 1, 0);
  }

  /**
   * Enter with a highlighted suggestion picks it (fills the box); otherwise it
   * confirms whatever is in the box. The two-step "pick then confirm" flow lets
   * the user see the selected product before it's added.
   */
  onEnter(): void {
    const suggestions = this.quickAddSuggestions;
    if (this.showQuickAddSuggestions && this.activeIndex >= 0 && this.activeIndex < suggestions.length) {
      this.selectSuggestion(suggestions[this.activeIndex]);
    } else {
      this.quickAdd();
    }
  }

  /** Escape closes the dropdown without changing the box. */
  onEscape(): void {
    this.quickAddFocused = false;
    this.activeIndex = -1;
  }

  /** Normalized de-dupe key: product_key when known, else lower-cased title. */
  private suggestionKey(productKey: string | null | undefined, title: string): string {
    if (productKey) return `p:${productKey}`;
    return `t:${title.trim().toLowerCase()}`;
  }

  /**
   * Unified, de-duplicated suggestion list. Active (pending) list items take
   * priority over bought (done) list items, which take priority over items
   * pulled from recent trips. Filtered by the current input text.
   */
  get quickAddSuggestions(): QuickAddSuggestion[] {
    const byKey = new Map<string, QuickAddSuggestion>();
    const add = (s: QuickAddSuggestion): void => {
      if (!byKey.has(s.key)) byKey.set(s.key, s);
    };

    // 1) Items already on the list, pending → "active".
    for (const i of this.items()) {
      if (i.state !== 'pending') continue;
      add({
        key: this.suggestionKey(i.product_key, i.title),
        title: i.title,
        product_key: i.product_key ?? null,
        product_id: i.product_id ?? null,
        client_id: i.client_id,
        source: 'active',
      });
    }
    // 2) Items already on the list, done → "bought".
    for (const i of this.items()) {
      if (i.state !== 'done') continue;
      add({
        key: this.suggestionKey(i.product_key, i.title),
        title: i.title,
        product_key: i.product_key ?? null,
        product_id: i.product_id ?? null,
        client_id: i.client_id,
        source: 'bought',
      });
    }
    // 3) Items from recent trips → "past".
    for (const r of this.recentTripItems) {
      add({
        key: this.suggestionKey(r.product_key, r.title),
        title: r.title,
        product_key: r.product_key ?? null,
        product_id: r.product_id ?? null,
        source: 'past',
      });
    }

    let result = [...byKey.values()];
    const q = this.quickAddText.trim().toLowerCase();
    if (q) result = result.filter(s => s.title.toLowerCase().includes(q));
    return result;
  }

  get showQuickAddSuggestions(): boolean {
    return this.quickAddFocused && this.quickAddSuggestions.length > 0;
  }

  suggestionLabel(source: QuickAddSuggestionSource): string {
    switch (source) {
      case 'active': return 'Auf Liste';
      case 'bought': return 'Gekauft';
      case 'past': return 'Letzter Einkauf';
    }
  }

  suggestionBadgeClass(source: QuickAddSuggestionSource): string {
    switch (source) {
      case 'active': return 'badge-info';
      case 'bought': return 'badge-success';
      case 'past': return 'badge-ghost';
    }
  }

  /**
   * Act on a chosen suggestion. Subtitles are intentionally ignored — only
   * the product (when known) or the bare title is added.
   *
   *   - active : already on the list (pending) → no-op, just dismiss.
   *   - bought : already on the list (done) → flip it back to pending.
   *   - past   : add a fresh item (by product when known, else raw title).
   */
  selectSuggestion(s: QuickAddSuggestion): void {
    // Fill the box with the picked product and remember it, but don't add yet —
    // the user confirms with Enter or the + button.
    this.quickAddText = s.title;
    this.selectedSuggestion = s;
    this.quickAddFocused = false;
    this.activeIndex = -1;
  }

  /** Perform the add for a confirmed suggestion, honoring its source. */
  private commitSuggestion(s: QuickAddSuggestion): void {
    if (s.source === 'active') {
      // Already on the list (pending) — nothing to add.
      this.toast.info(`„${s.title}" ist bereits auf der Liste`);
      return;
    }

    if (s.source === 'bought' && s.client_id) {
      // Re-adding a bought item flips it back to pending via a server PATCH,
      // so we can resolve the toast directly off the returned promise.
      const toastId = this.toast.loading(`„${s.title}" wird hinzugefügt …`);
      this.sync.toggleState(s.client_id)
        .then(() => this.toast.update(toastId, `„${s.title}" hinzugefügt`, {variant: 'success'}))
        .catch(() => this.toast.update(toastId, `„${s.title}" konnte nicht hinzugefügt werden`, {variant: 'error'}));
      return;
    }

    if (this.activeListId == null) return;
    const clientId = s.product_id != null
      ? this.sync.enqueueProduct(s.product_id, s.title, null)
      : this.sync.enqueueRawInput(s.title);
    this.trackAdd(clientId, s.title);
  }

  // ── Icons ────────────────────────────────────────────────────────────────────

  private get iconVariant(): 'black' | 'white' {
    return this.themeService.theme() === 'light' ? 'black' : 'white';
  }

  productIconUrl(productKindKey: string): string {
    return this.activeIconPack.iconUrl(productKindKey, this.iconVariant);
  }
}
